import * as sessionReplay from "@amplitude/session-replay-browser"
import { computed, get, set } from "@ember/object"
import Service, { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"
import type { User } from "@shared/models/user"
import { objectId } from "@shared/util/object-id"
import { analytics, handleError } from "cc-frontend/error-handling"
import findDocument from "cc-frontend/helpers/find-document"
import { getOrCreateAmplitudeSessionId } from "cc-frontend/lib/amplitude"
import { Base64 } from "js-base64"
import { isNil } from "lodash-es"
import ENV from "../config/environment"
import type EmberStore from "./ember-store"
import type Rpc from "./rpc"
import type SessionStore from "./session-store"
import type SocketService from "./socket"

function getRootDomain(): string | null {
  try {
    const hostname = window.location.hostname
    const hostnameParts = hostname.split(".")

    if (hostnameParts.length >= 2) {
      return hostnameParts.slice(-2).join(".")
    } else {
      return hostname
    }
  } catch (error) {
    console.error("Error getting root domain:", error)
    return null
  }
}

function parseJwt(token) {
  var base64Url = token.split(".")[1]
  var base64 = base64Url.replace("-", "+").replace("_", "/")
  return JSON.parse(Base64.decode(base64))
}

export default class Session extends Service {
  @service("ember-store") declare store: EmberStore
  @service declare socket: SocketService
  @service declare rpc: Rpc
  @service declare sessionStore: SessionStore
  @service dialog
  @service cookies

  id!: string
  // Proves this browser owns the session. The id is shared with other users; this never is.
  sessionToken = crypto.randomUUID()
  userId: string | null = null
  token: string | null = null
  browserId: string | null = null
  isImpersonating: boolean = false
  isAuthenticating: boolean | null = null
  isPrinting = false

  @findDocument("id", "session") session
  @findDocument("userId", "user") user

  /**
   * Sets:
   * - An ID for the session
   * - An ID for the browser if none exists
   *
   * Also:
   * - Inits Amplitude Session Replay.
   * - Creates a function on window for impersonating.
   */
  initiate() {
    let sessionId = objectId()
    set(this, "id", sessionId)

    if (window && window.sessionStorage) {
      if (ENV.environment === "production") {
        // Init Amplitude Session Replay, using the standalone SDK
        // (https://amplitude.com/docs/session-replay/session-replay-standalone-sdk).

        analytics.ready(() => {
          const segmentDeviceId = analytics.instance?.user().anonymousId()
          if (segmentDeviceId) {
            sessionReplay.init(ENV.AMPLITUDE_API_KEY!, {
              deviceId: segmentDeviceId,
              sessionId: getOrCreateAmplitudeSessionId(),
              trackServerUrl: "https://amp.commoncurriculum.workers.dev/ampr",
              configServerUrl: "https://amp.commoncurriculum.workers.dev/ampc",
              // @ts-expect-error
              experimental: {
                useWebWorker: true,
              },
              performanceConfig: {
                enabled: true,
              },
              // sampleRate is set remotely via Amplitude Session Replay settings.
            })
          } else {
            console.error("Failed to init Amplitude Session Replay: segmentDeviceId is not defined")
          }
        })
      }
    }

    if (ENV.environment !== "test") {
      let browserData = get(this, "sessionStore").fetchObject<{ id: string }>("browserData")
      if (browserData === null || typeof browserData.id !== "string") {
        browserData = get(this, "sessionStore").persistObject("browserData", {
          id: objectId(),
        })
      }
      set(this, "browserId", browserData.id)
      console.log(`session:${get(this, "id")}  browser:${get(this, "browserId")}`)
      get(this, "cookies").write("cc_session_id", get(this, "id"))
    }

    if (window) {
      // @ts-ignore
      window.CCSTOP_IMPERSONATING = () => {
        this.stopImpersonating()
        window.location.reload()
      }
    }

    if (ENV.environment !== "test") {
      this.restore()
    }
  }

  setTokenAndAuthenticate(payload) {
    this.setToken(payload.relationships.user.data.id, payload.attributes.token, false)
    this.socket.authenticateWithToken(payload.attributes.token)
  }

  setToken(userId, token, isImpersonating) {
    let sessionId = get(this, "id")
    get(this, "sessionStore").persistObject("sessionStore", {
      token: token,
      userId: userId,
      sessionId: sessionId,
      isImpersonating: isImpersonating,
    })
    // Cookie management moved to backend only
    set(this, "token", token)
    set(this, "userId", userId)
  }

  restore() {
    this.isAuthenticating = true
    let token
    let userId
    let isImpersonating

    if (ENV.environment === "test") return

    let sessionStore = get(this, "sessionStore").fetchObject<{
      token?: string
      userId?: string
      isImpersonating: boolean
    }>("sessionStore")
    if (sessionStore) {
      token = sessionStore.token
      userId = sessionStore.userId
      isImpersonating = sessionStore.isImpersonating
    }

    if (token) set(this, "token", token)
    if (userId) set(this, "userId", userId)
    if (isImpersonating) set(this, "isImpersonating", true)
    this.isAuthenticating = false
  }

  async signOut() {
    // Clear the backend cookie.
    await new Promise((resolve, reject) => this.rpc.execute("SIGN_OUT").then(resolve).catch(reject))

    // Clear frontend state.
    get(this, "sessionStore").clear("sessionStore")
    get(this, "store").clearMemory()
    set(this, "token", null)
    set(this, "userId", null)
    if (window && window.localStorage) {
      window.localStorage.setItem("signOutNow", "true")
      window.localStorage.removeItem("signOutNow")
    }
  }

  impersonate(userId, newToken) {
    if (get(this, "isImpersonating") === false) {
      get(this, "sessionStore").persistObject("originalSession", {
        token: get(this, "token"),
        userId: get(this, "userId"),
      })
    }
    set(this, "isImpersonating", true)
    this.setToken(userId, newToken, true)
    return userId
  }

  stopImpersonating() {
    let { userId, token } =
      get(this, "sessionStore").fetchObject<{ userId?: string; token?: string }>(
        "originalSession"
      ) || {}
    this.setToken(userId, token, false)
  }

  impersonateUserId(userId, newLocation) {
    // this.userId is the current user. Return if not existant
    if (isNil(this.userId)) return

    if (get(this, "isImpersonating")) {
      this.stopImpersonating()
      return
    } else {
      this.store.find("user", this.userId).then((currentUser: User) => {
        if (currentUser.attributes.isSupport === true) {
          get(this, "rpc")
            .execute("IMPERSONATE_BY_USER_ID", {
              userId: userId,
            })
            .then((data) => {
              this.impersonate(data.userId, data.token)
              // window.location.assign(newLocation) // if we don't want to open a new window
              window.open(newLocation)
            })
        } else {
          console.log("Must be logged into an account with support privleges.")
        }
      })
    }
  }

  @computed("token")
  get isAuthenticated() {
    return get(this, "token") !== null
  }

  mustSignInToProceed(location) {
    if (window) {
      analytics.track("Must Sign In Modal", {
        location: location,
      })
    }

    get(this, "dialog")
      .blank("dialogs/alert-must-sign-in-to-proceed", {
        className: "dialog--create-annotation",
      })
      .catch((error) => {
        // @ts-ignore
        if (error instanceof Error) handleError(error)
      })
  }

  @computed("user.isFulfilled", "user.attributes.isPro")
  get isPro(): boolean | undefined {
    // @ts-ignore
    return get(this, "user.attributes.isPro")
  }

  @computed("user.isFulfilled", "user.attributes.fiscalGroupId")
  get fiscalGroupId(): string {
    // @ts-ignore
    return get(this, "user.attributes.fiscalGroupId") || null
  }

  sendUserToAnalytics() {
    if (get(this, "isImpersonating") === true) return
    if (isNil(this.userId)) return
    this.store.find("user", this.userId).then((user: User) => {
      if (user === null || user === undefined) return
      // This is the bare minimum of what we need to send to analytics
      // Anything else we'll send from our backend. This list should be good
      // for any tools that use our client side sent analytics
      // and, as this might be the first identify call the analytics tool sees
      // a fine initial set of data to send.

      // Read the Dub.co click ID cookie (set by @dub/analytics when user arrives
      // via a Dub affiliate link). Included in identify so Dub's Segment destination
      // can create a lead conversion event.
      const dubClickId = (() => {
        if (typeof document === "undefined" || !document.cookie) return undefined
        const raw = document.cookie
          .split("; ")
          .find((row) => row.startsWith("dub_id="))
          ?.split("=")[1]
        try {
          return raw ? decodeURIComponent(raw) : undefined
        } catch {
          return raw
        }
      })()

      let attrs = {
        id: user.id,
        userId: user.id,
        cc_internal_user_id: user.id,
        sessionId: this.id,
        createdAt: user.attributes.createdAt,
        firstName: user.attributes.firstName,
        lastName: user.attributes.lastName,
        email: user.attributes.email,
        isPro: user.attributes.isPro,
        product: user.attributes.product,
        educationRole: user.attributes.educationRole,
        educationRoleSpecific: user.attributes.educationRoleSpecific,
        title: user.attributes.title,
        ...(dubClickId ? { dubClickId } : {}),
      }
      Sentry.configureScope((scope) => {
        scope.setUser(attrs)
        scope.setExtra(
          "adminPath",
          `https://www.commonplanner.com/admin/user-session/${get(user, "id")}?sessionId=${get(
            this,
            "id"
          )}`
        )
      })
      if (window) {
        analytics.identify(get(user, "id"), attrs, {
          integrations: {
            Intercom: {
              user_hash: user.attributes.hmacIntercom,
            },
          },
        })
      }
      try {
        if (window && window.gtag) {
          window.gtag("config", "GA_MEASUREMENT_ID", {
            user_id: user.id,
          })
          window.gtag("set", "user_properties", {
            isPro: user.attributes.isPro,
            product: user.attributes.product,
            createdAt: user.attributes.createdAt,
          })
        }
      } catch (error) {
        // Don't let errors in gtag crash the app.
        Sentry.captureException(error)
      }
      // @ts-ignore
      if (typeof ga !== "undefined") {
        // @ts-ignore
        ga("set", "userId", user.id)
      }
    })
  }
}
